<template>
  <template v-if="!isCheckingAvails && checkAvailsTotalImpressionsAvailable !== null">
    <div
      class="impression-count"
      :class="{
        bordered: showBorder,
        danger: checkAvailsResult !== AvailsResultEnum.Available,
        success: checkAvailsResult === AvailsResultEnum.Available,
      }">
      <div class="kpi">
        <div class="icon">
          <!-- success icon for Avails check -->
          <icon-check-avails-result-success v-if="checkAvailsResult === AvailsResultEnum.Available" />
          <!-- failure icon for Avails check -->
          <icon-check-avails-result-failure v-else />
        </div>
        <div class="value-and-label">
          <span class="value">{{ capitalize(checkAvailsResultFormatted) }}</span>
          <span class="label">{{ capitalize($t('names.status', { n: 1 })) }}</span>
        </div>
      </div>
      <div class="kpi-table">
        <div
          v-if="checkAvailsResult === AvailsResultEnum.MayBeAvailable && isStrictAvails"
          class="message">
          <span class="label">{{
            $t('messages.lineItemGoalCanBeReachedFromReservedImpressionsButNotAcrossAllDays')
          }}</span>
        </div>
        <div
          v-if="checkAvailsResult === AvailsResultEnum.NotAvailable && isStrictAvails"
          class="message">
          <span class="label">{{ $t('messages.lineItemGoalCannotBeReachedFromReservedImpressions') }}</span>
        </div>
        <table class="impressions-breakdown-table table table-striped">
          <thead>
            <tr v-if="membershipSettings?.uiEnablePredictiveAvails">
              <th class="confidence-index-row" />
              <th
                colspan="2"
                class="confidence-index-row">
                <div class="confidence-index">
                  <div
                    v-tooltip.left="$t('messages.confidenceIndexTooltip')"
                    class="badge badge-lg"
                    :class="confidenceIndexClassName">
                    {{ formattedConfidenceIndex }}
                  </div>
                </div>
              </th>
            </tr>
            <tr>
              <th />
              <th>{{ capitalize($t('messages.overallImpressions')) }}</th>
              <th>{{ capitalize($t('messages.audienceImpressions')) }}</th>
            </tr>
            <tr>
              <td>
                <b
                  v-if="shouldShowAvailsBreakdown"
                  style="display: flex; flex-direction: row">
                  <span v-if="membershipSettings?.uiEnablePredictiveAvails">
                    {{ capitalize($t('names.totalForecastedAvails', { n: 2 })) }}
                  </span>
                  <span v-else>
                    {{ capitalize($t('names.total', { n: 1 })) }}
                  </span>
                  <div
                    v-if="
                      organizationSettings?.enableOverbookedReservedImpressions &&
                      checkAvailsTotalOverbookedReservedImpressions != null &&
                      shouldShowAvailsBreakdown
                    "
                    v-tooltip="$t('messages.totalImpressionsOverbooked', { n: 1 })"
                    class="icon mr-2">
                    <icon-info class="icon" />
                  </div>
                </b>
              </td>
              <td>{{ checkAvailsTotalImpressionsAvailable }}</td>
              <td>{{ checkAvailsAudienceImpressionsAvailable }}</td>
            </tr>
            <tr v-if="checkAvailsTotalReservedImpressionsAvailable != null && shouldShowAvailsBreakdown">
              <template v-if="membershipSettings?.uiEnablePredictiveAvails">
                <td>
                  <b>{{ capitalize($t('messages.conservativeForecastedAvails')) }}&nbsp;</b>
                  <div
                    v-tooltip.left="capitalize($t('messages.minimumObservedAvailsInfoTooltip'))"
                    class="icon-info-total-col">
                    <icon-info />
                  </div>
                </td>
              </template>
              <template v-else>
                <td>
                  <b>{{ capitalize($t('states.reserved')) }}</b>
                </td>
              </template>
              <td>
                {{ checkAvailsTotalReservedImpressionsAvailable }}
                (
                <small>{{ checkAvailsTotalReservedImpressionsAvailablePercentage }}</small>
                )
              </td>
              <td>
                {{ checkAvailsAudienceReservedImpressionsAvailable }} (
                <small>{{ checkAvailsAudienceReservedImpressionsAvailablePercentage }}</small>
                )
              </td>
            </tr>
            <tr
              v-if="
                checkAvailsTotalReservedImpressionsAvailable != null &&
                shouldShowAvailsBreakdown &&
                !membershipSettings?.uiEnablePredictiveAvails
              ">
              <td>
                <b>{{ capitalize($t('states.unreserved')) }}</b>
              </td>
              <td>
                {{ checkAvailsTotalUnreservedImpressionsAvailable }}
                (
                <small>{{ checkAvailsTotalUnreservedImpressionsAvailablePercentage }}</small>
                )
              </td>
              <td>
                {{ checkAvailsAudienceUnreservedImpressionsAvailable }} (
                <small>{{ checkAvailsAudienceUnreservedImpressionsAvailablePercentage }}</small>
                )
              </td>
            </tr>
            <tr
              v-if="
                organizationSettings.enableOverbookedReservedImpressions &&
                checkAvailsTotalOverbookedReservedImpressions != null &&
                shouldShowAvailsBreakdown
              ">
              <td>
                <b>{{ capitalize($t('states.overbookedReserved')) }}</b>
              </td>
              <td>{{ checkAvailsTotalOverbookedReservedImpressions }}</td>
              <td>{{ checkAvailsAudienceOverbookedReservedImpressions }}</td>
            </tr>
          </thead>
        </table>
      </div>
    </div>

    <template v-if="isLoadingSites">
      <hr v-if="!showBorder" />
      <div class="flex-h m-3">
        <loader-small />
        <div class="ml-3">{{ capitalize($t('messages.loadingInventory')) }}...</div>
      </div>
    </template>
    <template v-if="!didFailToProcessSites && !isLoadingSites">
      <hr v-if="!showBorder" />

      <template v-if="membershipSettings?.uiEnablePredictiveAvails">
        <div class="predictive-avails-info">
          <i>{{ capitalize($t('messages.predictiveAvailsInfo')) }}</i>
        </div>
      </template>
    </template>
  </template>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';

  import { IconCheckAvailsResultFailure, IconCheckAvailsResultSuccess, IconInfo } from '@/common/icons/monochrome';
  import { DASH } from '@/globals';
  import { mapGetters, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  import AvailsConfidenceLevelEnum from './AvailsConfidenceLevel.enum';
  import AvailsResultEnum from './AvailsResult.enum';

  interface IProps {
    showBorder: boolean;
    isCheckingAvails: boolean;
    checkAvailsTotalImpressionsAvailable: string;
    checkAvailsResult: AvailsResultEnum;
    isStrictAvails: boolean;
    isLoadingSites: boolean;
    didFailToProcessSites: boolean;
    predictiveAvailsConfidenceLevel: AvailsConfidenceLevelEnum;
    shouldShowAvailsBreakdown: boolean;
    checkAvailsTotalOverbookedReservedImpressions: string;
    checkAvailsAudienceImpressionsAvailable: string;
    checkAvailsTotalReservedImpressionsAvailable: string;
    checkAvailsTotalReservedImpressionsAvailablePercentage: string;
    checkAvailsAudienceReservedImpressionsAvailable: string;
    checkAvailsAudienceReservedImpressionsAvailablePercentage: string;
    checkAvailsTotalUnreservedImpressionsAvailable: string;
    checkAvailsTotalUnreservedImpressionsAvailablePercentage: string;
    checkAvailsAudienceOverbookedReservedImpressions: string;
    checkAvailsAudienceUnreservedImpressionsAvailable: string;
    checkAvailsAudienceUnreservedImpressionsAvailablePercentage: string;
  }

  const props = defineProps<IProps>();

  const { organizationSettings } = mapOrgAppState();

  const { membershipSettings } = mapGetters();

  const checkAvailsResultFormatted = computed(() => {
    switch (props.checkAvailsResult) {
      case AvailsResultEnum.Available:
        return i18n.global.t('states.available');
      case AvailsResultEnum.NotAvailable:
        return i18n.global.t('states.notAvailable');
      case AvailsResultEnum.MayBeAvailable:
        return i18n.global.t('states.maybeAvailable');
      case AvailsResultEnum.PartiallyAvailable:
        return i18n.global.t('states.notAvailable');
      default:
        return i18n.global.t('states.notAvailable');
    }
  });

  const confidenceIndexClassName = computed(() => {
    switch (props.predictiveAvailsConfidenceLevel) {
      case AvailsConfidenceLevelEnum.Low:
        return 'badge-danger';
      case AvailsConfidenceLevelEnum.High:
        return 'badge-success';
      default:
        return 'badge-info';
    }
  });

  const confidenceIndexFormatted = computed(() => {
    switch (props.predictiveAvailsConfidenceLevel) {
      case AvailsConfidenceLevelEnum.Low:
        return capitalize(i18n.global.t('states.low'));
      case AvailsConfidenceLevelEnum.Medium:
        return capitalize(i18n.global.t('actions.medium'));
      case AvailsConfidenceLevelEnum.High:
        return capitalize(i18n.global.t('states.high'));
      default:
        return DASH;
    }
  });

  const formattedConfidenceIndex = computed(() =>
    [capitalize(i18n.global.t('messages.confidenceIndex')), confidenceIndexFormatted.value].join(': '),
  );
</script>

<style scoped lang="scss">
  .icon-info {
    margin: auto;
  }

  .form-section-content {
    .checking-avails {
      margin-top: 0;

      .loader {
        top: 0 !important;
      }
    }
  }

  .impression-count {
    display: flex;
    flex-direction: row;
    font-size: 1.4rem;
    font-weight: 400;
    padding: 1.2rem 2rem;
    margin: 1rem auto 1.6rem;

    &.bordered {
      border-width: 0.1rem;
      border-style: solid;
      padding: 1.6rem;
      margin-top: 3rem;

      &.success {
        border-color: var(--success);
        /*color: var(--successDark);*/
        background-color: var(--successFaded95);
        stroke: var(--textColor);

        svg {
          stroke: var(--info);
        }
      }

      &.danger {
        border-color: var(--error);
        /*color: var(--error);*/
        background-color: var(--errorFaded95);
        stroke: var(--textColor);

        svg {
          stroke: var(--info);
        }
      }
    }

    &.success {
      stroke: var(--success);
      svg {
        stroke: var(--info);
        height: 1.5rem;
        width: 1.5rem;
        margin-left: 0.2rem;
        &.hover {
          cursor: hand;
        }
      }
    }

    &.danger {
      stroke: var(--error);
      svg {
        stroke: var(--info);
        height: 1.5rem;
        width: 1.5rem;
        margin-left: 0.2rem;

        &.hover {
          cursor: hand;
        }
      }
    }

    .kpi {
      display: flex;
      align-content: center;
      flex-wrap: wrap;

      .icon {
        margin-right: 1rem;

        svg {
          height: 2.4rem;
          width: 2.4rem;
        }
      }

      .value-and-label {
        display: flex;
        flex-direction: column;
        margin-right: 6rem;

        .label {
          margin-top: 0.3rem;
          margin-bottom: 0.6rem;
          font-weight: 400;
          text-transform: uppercase;
          font-size: 1.2rem;
        }

        .value {
          font-weight: 600;
          font-size: 1.6rem;
        }

        .impressions-breakdown {
          display: flex;
          flex: 1 0 auto;
          flex-direction: column;
          padding-left: 0.6rem;

          .label {
            font-size: 1.1rem;
          }

          .value {
            font-size: 1.5rem;
          }
        }
      }
    }

    .kpi-table {
      flex-grow: 1;

      .confidence-index-row {
        margin-top: 0;
        border-top: 0;
      }
      .confidence-index {
        display: flex;
        flex-direction: row;
        justify-content: end;
      }
      .icon-info-total-col {
        display: inline-block;
        cursor: pointer;
      }
      .message {
        margin: 2rem;
      }

      .icon {
        margin-left: auto;

        svg {
          stroke: #ff0083;
          width: 2.4rem;
          height: 2.4rem;
        }
      }
    }
  }

  .export-breakdown {
    font-size: 1.4rem;
    padding: 0.8rem 1.6rem;
    display: flex;
    flex-direction: column;

    .label {
      display: flex;
      align-items: center;
      margin-bottom: 1.2rem;

      svg {
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 0.8rem;
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      margin-left: 4rem;

      .btn {
        margin-right: 1.2rem;
      }
    }
  }

  .predictive-avails-info {
    float: right;
  }
</style>
